import { Input, Select } from 'antd';
import Icon from 'assets/Icons';
import Button from 'components/Button';
import TableGiftCards from 'features/giftCards/components/TableGiftCards';
import giftCardsActions from 'features/giftCards/services/actions';
import giftCardsSelectors from 'features/giftCards/services/selectors';
import { DebouncedFunc } from 'lodash';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import refresh_icon from '../icons/change.svg';
import Box from 'components/Box';
import ButtonScanQRModal, { useButtonScanQRModalRef } from 'widgets/ButtonScanQR/ComponentRef';
import ModalAddGiftCard, { useModalAddGiftCardRef } from '../ModalAddGiftCard';
import redeemed_icon from '../icons/redeemed_icon.svg';
import RedeemModal, { IRedeemModalRef } from './RedeemModal';
import { GIFT_CARD_STATUS_OPTIONS, SOURCE_TYPE, SOURCE_TYPE_OPTIONS } from 'features/report/services/constants';
import PermissionManager from 'widgets/PermissionVerify';
import BaseRangePicker from 'components/BaseRangePicker';
import { IParamItemType } from 'features/report/services/types/reportStaff';
import dayjs, { Dayjs } from 'dayjs';
import { RangeValue } from 'rc-picker/lib/interface';
import { useAppDispatch } from 'store/hooks';

export type ITableGiftCardsProps = {
  PAGE_SIZE?: number;
  resetTime?: number | undefined;
  searchText?: string | undefined;
  pageSize?: number | undefined;
  resetTimeChecked: string[];
  status: string;
  setStatus: (value: string) => void;
  setResetTimeChecked: (value: any) => void;
  handleChangeResetTime: (value: number) => void;
  onReset: () => void;
  handleSearch: DebouncedFunc<(event: any) => void>;
  setSearchText?: (value: string) => void;
  setSourceType: (value: SOURCE_TYPE) => void;
  sourceType: SOURCE_TYPE;
};
const GiftCardTable = ({ handleSearch, onReset, setSearchText = () => undefined, PAGE_SIZE, resetTimeChecked, setResetTimeChecked, resetTime, searchText, status, setStatus, setSourceType, sourceType }: ITableGiftCardsProps) => {
  const addGiftCard = useModalAddGiftCardRef();
  const redeemModal = useRef<IRedeemModalRef>(null);
  const dispatch = useAppDispatch();

  const onOpenAddGiftCard = async () => {
    const result = await PermissionManager.verify(PermissionManager.PERMISSIONS.GIFT_CARD_ADD);
    if (!result || result?.skip) return addGiftCard.current?.open();
    if (!result.verifyData) return;
    addGiftCard.current?.open();
  };

  const handleRedeem = async () => {
    const result = await PermissionManager.verify(PermissionManager.PERMISSIONS.GIFT_CARD_REDEEM);
    if (!result || result?.skip) return redeemModal.current?.open();
    if (!result.verifyData) return;
    redeemModal.current?.open();
  };

  const convertDataToRangeDate = (date?: IParamItemType): [Dayjs | null, Dayjs | null] => {
    return [
      date?.startTime ? dayjs(date.startTime) : null,
      date?.endTime ? dayjs(date.endTime) : null,
    ];
  };

  const handleOnChangeDateRange = (dates: RangeValue<Dayjs>) => {
    console.log('dates', dates);
    dispatch(giftCardsActions.setParams({
      GIFT_CARDS: {
        startTime: dates?.[0]?.format('MM-DD-YYYY') || '',
        endTime: dates?.[1]?.format('MM-DD-YYYY') || '',
      } as IParamItemType,
    }));
  };

  return (
    <Container>
      <HeaderBox>
        <Box display='flex' style={{ flex: 1, position: 'relative' }}>
          <InputSearch searchText={searchText} handleSearch={handleSearch} setSearchText={setSearchText} />
        </Box>
        <div>
          <Select
            style={{ width: 150 }}
            value={sourceType}
            placeholder='Select source type'
            options={SOURCE_TYPE_OPTIONS}
            onChange={setSourceType}
          />
        </div>
        <div>
          <Select
            style={{ width: 150 }}
            value={status}
            placeholder='Select status'
            options={GIFT_CARD_STATUS_OPTIONS}
            onChange={setStatus}
          />
        </div>
        {/* <div className='DatePickerStyled'>
          <AppointmentDatePicker popupClassName='zoom-big-v2' size='middle' actions={giftCardsActions} selectors={giftCardsSelectors} type='GIFT_CARDS' />
        </div>
        <SwitchCalendarType actions={giftCardsActions} selectors={giftCardsSelectors} type='GIFT_CARDS' /> */}
        <div className='DatePickerStyled'>
          <BaseRangePicker 
            value={convertDataToRangeDate(giftCardsSelectors.getParams()?.GIFT_CARDS)} 
            format='MM-DD-YYYY'
            onChange={(dates) => handleOnChangeDateRange(dates)} 
          />
        </div>
        <Button iconElement={<img src={redeemed_icon} alt='redeemed_icon' style={{ width: 24, height: 24 }} />} ntype='DANGER' onClick={handleRedeem} >
          Card Redeemed
        </Button>
        <Button cardCashier disabled={!resetTimeChecked.length} iconElement={<img src={refresh_icon} alt='refresh_icon' style={{ width: 24, height: 24 }} />} ntype='LIGHT_BLUE' onClick={onReset}>
          Reset Gift
        </Button>
        <Button cardCashier iconElement={<Icon type="addWhite"></Icon>} ntype='PRIMARY' onClick={onOpenAddGiftCard}>
          Add Gift
        </Button>
      </HeaderBox>
      <TableGiftCards
        v2
        resetTime={resetTime}
        searchText={searchText}
        status={status}
        pageSize={PAGE_SIZE}
        resetTimeChecked={resetTimeChecked}
        setResetTimeChecked={setResetTimeChecked}
        sourceType={sourceType}
      />
      <ModalAddGiftCard ref={addGiftCard} />
      <RedeemModal ref={redeemModal} />
    </Container>
  );
};

export default GiftCardTable;

const InputSearch = ({ searchText, setSearchText, handleSearch }: { setSearchText: (value: string) => void, searchText?: string; handleSearch: (value: string) => void }) => {
  const qrRef = useButtonScanQRModalRef();
  const [text, setText] = useState(searchText);
  const onChange = (e: any) => {
    handleSearch(e);
    setText(e.target.value);
  };

  const onScanQrValue = (_value: string) => {
    const value = _value.replace(/[^0-9]/g, '');
    setText(value);
    setSearchText(value);
  };

  return (
    <>
      <InputStyled
        placeholder="Search by staff, customer, card number"
        value={text}
        onChange={onChange} />
      <ButtonScanQRStyled onClick={() => qrRef.current?.scan()} type='button'><Icon type='QR' style={{ zoom: 1.5 }} /></ButtonScanQRStyled>
      <ButtonScanQRModal ref={qrRef} onScan={onScanQrValue} />
    </>
  );
};
const ButtonScanQRStyled = styled.button`
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 0;
  border: 1px solid #86909C;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  &:hover {
    background: #eee;
  }
`;
const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
  padding: 0 1rem;
  .ant-select .ant-select-selection-item {
    height: auto;
  }
  .ant-select .ant-select-arrow {
    color: #1D2129;
    font-size: 18px;
    transform: scaleX(1.1) scaleY(0.9);
  }
  .ant-select .ant-select-selector {
    background: #ffffff;
    border: 1px solid #86909C;
    .ant-select-selection-item-content {
      color: #1D2129;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
  .ant-select .ant-select-selector {
    background: #ffffff;
    border: 1px solid #86909C;
    .ant-select-selection-placeholder, .ant-select-selection-search-input, .ant-select-selection-item {
      color: rgb(29, 33, 41);
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }

  .ant-input-affix-wrapper {
    background: #ffffff;
    border: 1px solid #86909C;
    .ant-input {
      background: transparent;
      color: rgb(29, 33, 41);
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }

  .DatePickerStyled .ant-picker {
  }
  .DatePickerStyled .CONTENT_2, .DatePickerStyled .ant-picker-input input {
    background: transparent;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }


  .SwitchCalendarTypeStyled {
    .GroupButtons {
      background: #E5E6EB;
      border: 1px solid #E5E6EB;
    }
    button {
      padding-right: 16px;
      padding-left: 16px;
      width: auto;
      .BODY_1 {
        color: #1D2129;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 30px; /* 166.667% */
        text-transform: capitalize;
      }
    }
    button.active {
      border-radius: 4px;
      background: #232F3E;
      .BODY_1 {
        color: #FFF;
      }
    }
  }
`;

const InputStyled = styled(Input)`
    height: 3.5rem;
    width:100%;
    display: flex;
    flex:1;
`;
const Container = styled.div`
display: flex;
padding: 16px;
flex-direction: column;
align-items: flex-start;
align-self: stretch;
gap: 16px;
border-radius: 8px;
border: 1px solid #CCD4DC;
background: #FFF;
zoom: 1.3;
.gift-card-title {
  align-self: stretch;
  color: #1D2129;
  font-family: Poppins;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
  .TableGiftCardsStyled {
    align-self: stretch;
    .card-number, .BODY_1 {
      font-family: Poppins;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
    .ant-table-thead>tr>th {
      background : #6FABB6;
      border-radius: 0;
      padding-left: 4px;
      padding-right: 4px;
      border-start-start-radius: 0 !important;
      border-start-end-radius: 0 !important;
      color: #fff;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      text-align: left !important;

      &::before {
        height: 100% !important;
        z-index: 1;
      }
    }
  }
`;
